import { useState } from 'react';
import { PhoneOutgoing, Loader2 } from 'lucide-react';
import { useCentralita, llamar, normalizar } from '@/hooks/useCentralita';
import { soportaCentralita } from './AnclaCCP';

/**
 * Llama a este número.
 *
 * Se pone junto a cualquier teléfono del CRM. Se apaga solo cuando no se puede
 * llamar —navegador sin soporte, centralita aún cargando, otra llamada en
 * curso— y dice por qué al pasar por encima, en vez de desaparecer: un botón
 * que a veces está y a veces no es más desconcertante que uno apagado.
 *
 * El nombre y el alojamiento no son decoración: se guardan al marcar para que
 * la barra de llamada diga a quién estás llamando, ya que Amazon Connect solo
 * conoce el número.
 *
 * `onLlamada` avisa cuando la llamada ha salido de verdad, no cuando se pulsa:
 * sirve para dejar constancia en el historial de la ficha sin apuntar intentos
 * que la centralita rechazó.
 */
export default function BotonLlamar({
  numero,
  nombre = null,
  alojamiento = null,
  compacto = false,
  onLlamada,
}: {
  numero: string | null | undefined;
  nombre?: string | null;
  alojamiento?: string | null;
  compacto?: boolean;
  /** Se llama solo si la llamada ha salido. */
  onLlamada?: () => void;
}) {
  const { estado } = useCentralita();
  const [enviando, setEnviando] = useState(false);
  const e164 = numero ? normalizar(numero) : null;

  if (!e164 || !soportaCentralita()) return null;

  const motivo =
    estado === 'libre' ? null
    : estado === 'cargando' ? 'La centralita todavía está cargando'
    : estado === 'sin-sesion' ? 'Inicia sesión en la centralita para poder llamar'
    : estado === 'sin-iniciar' ? 'La centralita no está disponible'
    : 'Ya hay una llamada en curso';

  const pulsar = async (e: React.MouseEvent) => {
    e.stopPropagation();   // vive dentro de filas que abren el detalle al pulsar
    e.preventDefault();
    setEnviando(true);
    try {
      await llamar(e164, { nombre, alojamiento });
      onLlamada?.();
    } catch {
      // El error queda en el estado de la centralita y lo enseña la barra.
    } finally {
      setEnviando(false);
    }
  };

  return (
    <button
      type="button"
      onClick={pulsar}
      disabled={Boolean(motivo) || enviando}
      title={motivo ?? `Llamar a ${nombre ?? e164}`}
      aria-label={`Llamar a ${nombre ?? e164}`}
      className={`rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed
        text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-500/10
        ${
          compacto
            ? 'grid place-items-center h-9 w-9'
            : 'inline-flex items-center justify-center h-10 px-3 gap-1.5 text-sm font-medium'
        }`}
    >
      {enviando ? <Loader2 className="animate-spin" size={15} /> : <PhoneOutgoing size={15} />}
      {!compacto && 'Llamar'}
    </button>
  );
}
